// ============================================================================
// RECEIPT VOUCHER — form + view modals (ADR-0054). Mirror of the Payment Voucher.
//
// The one-step receipt a business unit records itself: the money reaches a cash or
// bank account and, in the same posting, settles what the payer owed. (The Treasury
// receipt — capture, clearing, worksheet — is the centralised two-step flow.)
//
// The cash is never typed. Each section contributes to
//
//     Net Received = what adds − what reduces
//
// which is exactly what the bank is debited with. Bills clear at the rate they were
// booked at; the difference against the receipt's rate is an exchange gain or loss.
// Create-only: a posted receipt is cancelled and re-entered, never edited.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { usePaymentModeOptions } from 'hooks/usePaymentModeOptions';
import { ConfirmDialog } from 'components/modals';
import OutstandingDisplay from 'components/common/OutstandingDisplay';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import { receiptVoucherService, coaLedgerService, partyService, entityAccountingConfigService } from 'api/services';
import { RECEIPT_LINE_TYPES, RECEIVE_AS, isReceiptPartyLine } from 'api/types';
import type { ReceiptVoucherDto, CreateReceiptVoucherRequest, CreateReceiptVoucherLineRequest, DeductionReasonDto } from 'api/types';
import type { StatementLinePrefill } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

const INSTRUMENT_OPTIONS: SelectOption[] = [
  { value: 'TRANSFER', label: 'Bank transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
];

const RECEIVE_AS_OPTIONS: SelectOption[] = [
  { value: RECEIVE_AS.CUSTOMER_ADVANCE, label: 'Customer advance' },
  { value: RECEIVE_AS.SUPPLIER_REFUND, label: 'Supplier refund' },
];

const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => { const n = parseFloat(String(s).replace(/,/g, '')); return isNaN(n) ? 0 : n; };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// STATE
// ---------------------------------------------------------------------------

interface BillRow {
  openItemId: string;
  referenceNo: string;
  dueDate: string;
  /** The item's own currency (ADR-0031). */
  currencyCode: string;
  fcOutstanding: number;
  /** Outstanding in base, at the item's booked rate. */
  baseOutstanding: number;
  /** "Receiving" — ALWAYS in the RECEIPT's currency: the money reaching the account. */
  allocated: string;
  /** "Settling" — the ITEM's currency discharged; only when it differs from the receipt's. */
  settling: string;
  /**
   * "Short-paid" (ADR-0060) — in the ITEM's currency: what the customer deducted. The bill closes
   * for it and it stays open as a short-paid item. Editing it moves Receiving the other way, so
   * the bill stays closed.
   */
  short: string;
  reason: string;
  remarks: string;
  /** When the item itself is a short-paid amount left by an earlier receipt — what it is. */
  deductionNote?: string;
}

interface PartySection {
  key: number;
  partyId: string;
  partyLabel: string;
  amount: string;
  bills: BillRow[];
  loadingBills: boolean;
  /** Where an unallocated remainder lands — stated, never inferred. */
  receiveAs: string;
}

interface GlRow {
  key: number;
  ledgerId: string;
  ledgerLabel: string;
  amount: string;
  /** TRUE adds to the receipt (Cr — other income); FALSE reduces it (Dr — WHT, bank charges, discount). */
  isCredit: boolean;
  narration: string;
}

interface ReceiptForm {
  receiptDate: string;
  depositToLedgerId: string;
  depositToLedgerLabel: string;
  /** The account's currency, which is the receipt's. */
  currencyCode: string;
  exchangeRate: string;
  instrumentType: string;
  instrumentNumber: string;
  instrumentDate: string;
  drawerBank: string;
  narration: string;
}

const initialForm = (): ReceiptForm => ({
  receiptDate: today(),
  depositToLedgerId: '', depositToLedgerLabel: '', currencyCode: '', exchangeRate: '1',
  instrumentType: 'TRANSFER', instrumentNumber: '', instrumentDate: '', drawerBank: '',
  narration: '',
});

export interface ReceiptVoucherFormModalProps {
  isOpen: boolean;
  onPosted: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onRestoreModal?: (instanceId?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /** Posting from a bank statement line (ADR-0055 §5): account, date and amount come from the line. */
  fromStatementLine?: StatementLinePrefill;
  /** Replaces the plain create — the statement posts the voucher and matches it to the line. */
  onSubmitOverride?: (payload: CreateReceiptVoucherRequest) => Promise<unknown>;
}

export const ReceiptVoucherFormModal: React.FC<ReceiptVoucherFormModalProps> = ({
  isOpen, onPosted, onClose, tabId, moduleName, createInstanceId, onRestoreModal, minimizeRef, fromStatementLine, onSubmitOverride,
}) => {
  const { minimize } = useModalMinimize();
  const { selectedBusinessUnit } = useAuthContext();
  const sessionBuId = selectedBusinessUnit?.id;
  const fy = useSelectedFinancialYear();

  const keySeq = useRef(0);
  const nextKey = () => ++keySeq.current;

  const [form, setForm] = useState<ReceiptForm>(initialForm);

  // Payment modes come from the company-wide master (ADR-0055 §6); the static list is the fallback.

  const modeOptions = usePaymentModeOptions(INSTRUMENT_OPTIONS, form.instrumentType);
  const [parties, setParties] = useState<PartySection[]>([]);
  const [glRows, setGlRows] = useState<GlRow[]>([]);
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [reasonOptions, setReasonOptions] = useState<SelectOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const partyRef = useRef(parties); partyRef.current = parties;
  const glRef = useRef(glRows); glRef.current = glRows;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const ledgerLabelCache = useRef(new Map<string, string>());
  const ledgerCurrencyCache = useRef(new Map<string, string>());
  const partyLabelCache = useRef(new Map<string, string>());

  /** Dirty snapshot — user-editable values only. */
  const snapshot = useCallback((f: ReceiptForm, ps: PartySection[], gl: GlRow[]) =>
    JSON.stringify({
      f: { ...f, depositToLedgerLabel: '' },
      ps: ps.map(x => ({ p: x.partyId, a: x.amount, ra: x.receiveAs, b: x.bills.map(b => [b.openItemId, b.allocated, b.settling, b.short, b.reason, b.remarks]) })),
      gl: gl.map(x => ({ l: x.ledgerId, a: x.amount, cr: x.isCredit, n: x.narration })),
    }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    entityAccountingConfigService.getConfig()
      .then(c => setFunctionalCurrency(c?.functionalCurrencyCode || ''))
      .catch(() => setFunctionalCurrency(''));
    receiptVoucherService.getDeductionReasons()
      .then((rs: DeductionReasonDto[]) => setReasonOptions(rs.map(x => ({ value: x.value, label: x.label }))))
      .catch(() => setReasonOptions([]));
    if (isRestoreRef.current) { isRestoreRef.current = false; setErrors({}); return; }
    const f = fromStatementLine
      ? { ...initialForm(), receiptDate: fromStatementLine.date, depositToLedgerId: fromStatementLine.bankLedgerId, depositToLedgerLabel: fromStatementLine.bankLedgerLabel,
          instrumentNumber: (fromStatementLine.reference || '').slice(0, 50), narration: fromStatementLine.description || '' }
      : { ...initialForm(), receiptDate: defaultDateIn(fy) };
    if (fromStatementLine) {
      // The account's currency decides the voucher's (a foreign account then asks for the rate).
      Promise.all([coaLedgerService.getById(fromStatementLine.bankLedgerId), entityAccountingConfigService.getConfig()])
        .then(([l, cfg]) => {
          const ccy = l?.data?.currencyCode || '';
          const foreign = !!ccy && !!cfg?.functionalCurrencyCode && ccy !== cfg.functionalCurrencyCode;
          setForm(p => ({ ...p, currencyCode: ccy, exchangeRate: foreign ? '' : '1' }));
        })
        .catch(() => { /* home currency assumed */ });
    }
    setForm(f); setParties([]); setGlRows([]);
    initialSnapshotRef.current = snapshot(f, [], []);
    setErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return snapshot(form, parties, glRows) !== initialSnapshotRef.current;
  }, [form, parties, glRows, snapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const idx = createInstanceIdRef.current || '1';
    const savedForm = { ...formRef.current };
    const savedParties = partyRef.current.map(x => ({ ...x, bills: x.bills.map(b => ({ ...b })) }));
    const savedGl = glRef.current.map(x => ({ ...x }));
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? snapshot(savedForm, savedParties, savedGl) !== savedSnapshot : false;
    const who = savedParties[0]?.partyLabel;
    minimize({
      id: `${tabId}-form-modal-create-${idx}`,
      title: who ? `Receipt - ${who} (${idx})` : `Receipt (${idx})`,
      moduleName: moduleName || 'Receipt Vouchers', tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm); setParties(savedParties); setGlRows(savedGl);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(idx);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, snapshot]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const err = (f: string) => errors[f];
  const clearErr = useCallback((f: string) =>
    setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; }), []);
  const updateForm = useCallback((patch: Partial<ReceiptForm>) => setForm(p => ({ ...p, ...patch })), []);

  // --- Option loaders -------------------------------------------------------

  const loadCashBank = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await coaLedgerService.getAll({
      pageSize: 100, searchTerm: input || undefined, ledgerKind: 'GL', availableToBusinessUnitId: sessionBuId,
    });
    return res.data
      .filter(l => l.subtypeCode === 'BANK_ACCOUNT' || l.subtypeCode === 'CASH_ACCOUNT')
      .map(l => {
        const label = `${l.ledgerCode} - ${l.ledgerName}`;
        ledgerLabelCache.current.set(l.id, label);
        // The account's currency decides the receipt's — money cannot reach a pula account in dollars.
        ledgerCurrencyCache.current.set(l.id, l.currencyCode || '');
        return { value: l.id, label };
      });
  }, [sessionBuId]);

  /** Nominal ledgers for the account lines — party subledgers stay out (reached through the party). */
  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await coaLedgerService.getAll({
      pageSize: 50, searchTerm: input || undefined, ledgerKind: 'GL', availableToBusinessUnitId: sessionBuId,
    });
    return res.data.map(l => {
      const label = `${l.ledgerCode} - ${l.ledgerName}`;
      ledgerLabelCache.current.set(l.id, label);
      return { value: l.id, label };
    });
  }, [sessionBuId]);

  /** Defaults to who owes us, largest first; widens to the full party master once the user types. */
  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    const owing = await receiptVoucherService.getReceivableParties(input || undefined, 25).catch(() => []);
    const opts: SelectOption[] = owing.map(o => {
      const what = o.hasSupplierDebits && !o.hasCustomerBills ? 'refund due' : 'owes';
      partyLabelCache.current.set(o.partyId, `${o.partyCode} — ${o.partyName}`);
      return { value: o.partyId, label: `${o.partyCode} — ${o.partyName}  ·  ${what} ${money(o.amountDue)}` };
    });
    if (!input) return opts;
    const seen = new Set(opts.map(o => o.value));
    const all = await partyService.getAll({ pageSize: 25, searchTerm: input });
    for (const p of all.data) {
      if (seen.has(p.id)) continue;
      const label = `${p.partyCode} — ${p.partyName}`;
      partyLabelCache.current.set(p.id, label);
      opts.push({ value: p.id, label });
    }
    return opts;
  }, []);

  // --- Section management ---------------------------------------------------

  const addParty = useCallback(() => {
    setParties(p => [...p, {
      key: nextKey(), partyId: '', partyLabel: '', amount: '', bills: [],
      loadingBills: false, receiveAs: RECEIVE_AS.CUSTOMER_ADVANCE,
    }]);
  }, []);

  const addGlRow = useCallback((isCredit = true) => {
    setGlRows(p => [...p, { key: nextKey(), ledgerId: '', ledgerLabel: '', amount: '', isCredit, narration: '' }]);
  }, []);

  const updateParty = useCallback((key: number, patch: Partial<PartySection>) =>
    setParties(p => p.map(s => (s.key === key ? { ...s, ...patch } : s))), []);
  const updateGl = useCallback((key: number, patch: Partial<GlRow>) =>
    setGlRows(p => p.map(g => (g.key === key ? { ...g, ...patch } : g))), []);

  /** Load what this party owes us — invoices and a supplier's debit balance alike. */
  const pickParty = useCallback(async (key: number, partyId: string) => {
    clearErr(`party-${key}`);
    updateParty(key, { partyId, partyLabel: partyLabelCache.current.get(partyId) || '', bills: [], loadingBills: !!partyId });
    if (!partyId) return;
    try {
      const items = await receiptVoucherService.getOpenItems(partyId);
      updateParty(key, {
        loadingBills: false,
        bills: items.map(i => ({
          openItemId: String(i.id),
          referenceNo: i.referenceNo,
          dueDate: (i.dueDate || i.itemDate || '').slice(0, 10),
          currencyCode: i.currencyCode || functionalCurrency,
          fcOutstanding: i.fcBalance,
          baseOutstanding: i.baseBalance,
          allocated: '',
          settling: '',
          short: '',
          reason: '',
          remarks: '',
          deductionNote: i.deductionKind
            ? ['Short-paid', i.sourceReferenceNo ? `on ${i.sourceReferenceNo}` : '', i.reasonLabel || i.reasonCode || '', i.remarks || ''].filter(Boolean).join(' · ')
            : undefined,
        })),
      });
    } catch {
      updateParty(key, { bills: [], loadingBills: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateParty, clearErr, functionalCurrency]);

  // ---- Cross-currency settlement helpers (mirror of the server's ReceivableSettlement) ----
  // "Receiving" is ALWAYS in the RECEIPT's currency. When the item's currency differs, "Settling"
  // states how much of the ITEM's currency it discharges, and the exchange difference is the money
  // in base minus the discharge at the item's booked rate — positive = GAIN.
  const fnCcyRef = useRef(functionalCurrency); fnCcyRef.current = functionalCurrency;
  const rcptCcyOf = () => formRef.current.currencyCode || fnCcyRef.current;
  const rcptRateOf = () => { const r = num(formRef.current.exchangeRate); return r > 0 ? r : 1; };
  const isCrossBill = (b: BillRow) => b.currencyCode !== rcptCcyOf();
  const billRateOf = (b: BillRow) => (b.fcOutstanding > 0 ? b.baseOutstanding / b.fcOutstanding : 1);
  const bookedRcptEquiv = (b: BillRow) => r2(b.baseOutstanding / rcptRateOf());
  const billFcSettling = (b: BillRow) =>
    isCrossBill(b) ? Math.min(num(b.settling), b.fcOutstanding) : Math.min(num(b.allocated), b.fcOutstanding);
  /** Realized FX in base: positive = gain (credits FX Gain / Loss), negative = loss. */
  const billFx = (b: BillRow) => {
    const receiving = num(b.allocated);
    const fc = billFcSettling(b);
    if (fc <= 0 || receiving <= 0) return 0;
    return isCrossBill(b)
      ? r2(receiving * rcptRateOf() - fc * billRateOf(b))
      : r2(fc * rcptRateOf() - fc * billRateOf(b));
  };

  /** Spread the typed amount oldest-first; every cell stays editable. */
  const autoAllocate = useCallback((key: number) => {
    setParties(p => p.map(s => {
      if (s.key !== key) return s;
      let left = num(s.amount);
      return {
        ...s,
        bills: s.bills.map(b => {
          const cap = isCrossBill(b) ? bookedRcptEquiv(b) : b.fcOutstanding;
          const a = Math.min(left, cap);
          left = r2(left - a);
          if (a <= 0) return { ...b, allocated: '', settling: '' };
          const settling = isCrossBill(b)
            ? (a >= cap ? b.fcOutstanding : Math.min(r2((a * rcptRateOf()) / billRateOf(b)), b.fcOutstanding))
            : 0;
          return { ...b, allocated: String(r2(a)), settling: isCrossBill(b) ? String(settling) : '' };
        }),
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ADR-0060: a short-paid amount, in the bill's currency, capped at what is left once the money is counted. */
  const billShort = (b: BillRow) => Math.max(0, Math.min(num(b.short), b.fcOutstanding));
  /** A short-paid bill without one loses its reason and remarks too. */
  const withShort = (b: BillRow, short: number): BillRow =>
    short > 0.004 ? { ...b, short: String(r2(short)) } : { ...b, short: '', reason: '', remarks: '' };
  /** The receipt-currency money for a bill-currency figure, at the bill's booked rate (cross-currency bills). */
  const rcptEquivOf = (b: BillRow, fc: number) => r2((fc * billRateOf(b)) / rcptRateOf());

  const setBillAmount = useCallback((key: number, openItemId: string, field: 'receiving' | 'settling', value: string) => {
    setParties(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        const hadShort = billShort(b) > 0;
        if (field === 'settling') {
          const settling = value === '' ? '' : String(Math.min(num(value), b.fcOutstanding));
          // A short-paid bill stays closed: what is not settled by money is short-paid.
          return hadShort ? withShort({ ...b, settling }, b.fcOutstanding - num(settling)) : { ...b, settling };
        }
        // Capped at the outstanding only when the currencies match (amount received IS amount settled).
        const capped = isCrossBill(b) ? value : (value === '' ? '' : String(Math.min(num(value), b.fcOutstanding)));
        if (hadShort && !isCrossBill(b)) return withShort({ ...b, allocated: capped }, b.fcOutstanding - num(capped));
        return { ...b, allocated: capped };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Editing Short-paid moves Receiving the other way (ADR-0060): Receiving = Outstanding − Short-paid,
   * so the bill stays closed. On a bill in another currency, Settling takes the rest and Receiving
   * follows at the booked rate. Short-paid in full leaves nothing received — allowed.
   */
  const setShort = useCallback((key: number, openItemId: string, value: string) => {
    setParties(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        const sh = value === '' ? 0 : Math.min(Math.max(0, num(value)), b.fcOutstanding);
        const rest = r2(b.fcOutstanding - sh);
        const next = withShort(b, sh);
        if (isCrossBill(b))
          return { ...next, settling: rest > 0 ? String(rest) : '', allocated: rest > 0 ? String(rcptEquivOf(b, rest)) : '' };
        return { ...next, allocated: rest > 0 ? String(rest) : '' };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setShortField = useCallback((key: number, openItemId: string, patch: Partial<Pick<BillRow, 'reason' | 'remarks'>>) => {
    setParties(p => p.map(s => (s.key !== key ? s : { ...s, bills: s.bills.map(b => (b.openItemId === openItemId ? { ...b, ...patch } : b)) })));
  }, []);

  /**
   * "Close ticked bills in full" (ADR-0060): the amount received fills the ticked bills oldest first;
   * whatever each ticked bill does not receive becomes its short-paid amount, so every ticked bill
   * closes. The user can then move the short-paid amount between bills; reasons already picked stay.
   */
  const closeTickedInFull = useCallback((key: number) => {
    setParties(p => p.map(s => {
      if (s.key !== key) return s;
      let left = num(s.amount);
      return {
        ...s,
        bills: s.bills.map(b => {
          const ticked = num(b.allocated) > 0 || num(b.settling) > 0 || billShort(b) > 0;
          if (!ticked) return b;
          if (isCrossBill(b)) {
            const cap = bookedRcptEquiv(b);
            const a = Math.max(0, Math.min(left, cap));
            left = r2(left - a);
            const settling = a <= 0 ? 0 : a >= cap ? b.fcOutstanding : Math.min(r2((a * rcptRateOf()) / billRateOf(b)), b.fcOutstanding);
            return withShort({ ...b, allocated: a > 0 ? String(r2(a)) : '', settling: settling > 0 ? String(settling) : '' }, b.fcOutstanding - settling);
          }
          const a = Math.max(0, Math.min(left, b.fcOutstanding));
          left = r2(left - a);
          return withShort({ ...b, allocated: a > 0 ? String(r2(a)) : '', settling: '' }, b.fcOutstanding - a);
        }),
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBill = useCallback((key: number, openItemId: string, on: boolean) => {
    setParties(p => p.map(s => (s.key !== key ? s : {
      ...s,
      bills: s.bills.map(b => {
        if (b.openItemId !== openItemId) return b;
        if (!on) return { ...b, allocated: '', settling: '', short: '', reason: '', remarks: '' };
        return isCrossBill(b)
          ? { ...b, settling: String(b.fcOutstanding), allocated: String(bookedRcptEquiv(b)) }
          : { ...b, allocated: String(b.fcOutstanding), settling: '' };
      }),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching the deposit account can change the receipt's currency, which changes what every
  // typed figure MEANS — clear the allocations rather than reinterpret them silently.
  const prevCcy = useRef<string>('');
  useEffect(() => {
    const ccy = form.currencyCode || functionalCurrency;
    if (prevCcy.current && ccy && prevCcy.current !== ccy) {
      setParties(p => p.map(s => ({ ...s, bills: s.bills.map(b => (b.allocated || b.settling || b.short ? { ...b, allocated: '', settling: '', short: '', reason: '', remarks: '' } : b)) })));
    }
    prevCcy.current = ccy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currencyCode, functionalCurrency]);

  // --- Derived totals -------------------------------------------------------

  const partyTotals = useMemo(() => parties.map(s => {
    const allocated = r2(s.bills.reduce((t, b) => t + num(b.allocated), 0));
    const asked = num(s.amount);
    const total = asked > 0 ? asked : allocated;   // empty amount = "receive exactly what I ticked"
    // ADR-0060: short-paid amounts kept open, in base at each bill's booked rate (bills may differ in currency).
    const shortBase = r2(s.bills.reduce((t, b) => t + billShort(b) * billRateOf(b), 0));
    const shortCount = s.bills.filter(b => billShort(b) > 0).length;
    return { key: s.key, allocated, asked, total, remainder: r2(Math.max(0, total - allocated)), shortBase, shortCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [parties]);

  const totals = useMemo(() => {
    const partyTotal = partyTotals.reduce((t, s) => t + s.total, 0);
    const glAdds = glRows.filter(g => g.isCredit).reduce((t, g) => t + num(g.amount), 0);
    const glReduces = glRows.filter(g => !g.isCredit).reduce((t, g) => t + num(g.amount), 0);
    const gross = r2(partyTotal + glAdds);
    const reduces = r2(glReduces);
    return { gross, reduces, net: r2(gross - reduces) };
  }, [partyTotals, glRows]);

  const anySection = parties.length > 0 || glRows.length > 0;
  const isForeignReceipt = !!form.currencyCode && !!functionalCurrency && form.currencyCode !== functionalCurrency;
  const rcptCurrency = form.currencyCode || functionalCurrency;
  const totalFx = r2(parties.reduce((x, s) => x + s.bills.reduce((y, b) => y + billFx(b), 0), 0));

  // --- Save -----------------------------------------------------------------

  const validate = useCallback((): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.receiptDate) e.receiptDate = 'Receipt date is required';
    if (!form.depositToLedgerId) e.depositToLedgerId = 'Choose the cash or bank account the money reached';
    if (isForeignReceipt && !(Number(form.exchangeRate) > 0)) {
      e.exchangeRate = `Enter today's rate for ${form.currencyCode} — bills clear at the rate they were booked at, and the difference is posted as an exchange gain or loss`;
    }
    if (!anySection) e.sections = 'Add a payer or an account to say what the money is for';

    parties.forEach(s => {
      const t = partyTotals.find(x => x.key === s.key)!;
      if (!s.partyId) { e[`party-${s.key}`] = 'Pick who paid'; return; }
      if (t.total <= 0 && t.shortCount === 0) { e[`party-${s.key}`] = 'Enter the amount received, or tick the bills being settled'; return; }
      const noReason = s.bills.find(b => billShort(b) > 0 && !b.reason);
      if (noReason) { e[`party-${s.key}`] = `${noReason.referenceNo} is short-paid — pick a reason`; return; }
      const over = s.bills.find(b => (isCrossBill(b) ? num(b.settling) : num(b.allocated)) + billShort(b) > b.fcOutstanding + 0.004);
      if (over) { e[`party-${s.key}`] = `${over.referenceNo}: receiving and short-paid together are more than its ${over.currencyCode} ${money(over.fcOutstanding)} open`; return; }
      if (t.allocated > t.total + 0.004) {
        e[`party-${s.key}`] = `Allocated ${rcptCurrency} ${money(t.allocated)} is more than the ${rcptCurrency} ${money(t.total)} received`;
        return;
      }
      for (const b of s.bills) {
        if (b.currencyCode === rcptCurrency) continue;
        const receiving = num(b.allocated), settling = num(b.settling);
        if (receiving > 0 && settling <= 0) {
          e[`party-${s.key}`] = `${b.referenceNo} is in ${b.currencyCode}: say how much ${b.currencyCode} the ${rcptCurrency} ${money(receiving)} settles, so the exchange difference can be posted`;
          return;
        }
        if (settling > 0 && receiving <= 0) {
          e[`party-${s.key}`] = `${b.referenceNo}: settling ${b.currencyCode} ${money(settling)} needs the ${rcptCurrency} amount received for it`;
          return;
        }
      }
    });

    glRows.forEach(g => {
      if (!g.ledgerId) e[`gl-${g.key}`] = 'Pick the account to post to';
      else if (num(g.amount) <= 0) e[`gl-${g.key}`] = 'Enter an amount';
    });

    if (!e.sections && totals.gross <= 0) e.sections = 'Nothing is being received — add what the money is for.';
    else if (!e.sections && totals.net <= 0) e.sections = 'The deductions cancel out what is being received, so nothing would reach the bank.';
    if (fromStatementLine && !e.sections && Math.abs(totals.net - fromStatementLine.amount) > 0.004) {
      e.sections = `The bank credited ${money(fromStatementLine.amount)} — this receipt nets ${money(totals.net)}. They must be equal.`;
    }
    return e;
  }, [form, parties, partyTotals, glRows, anySection, totals, rcptCurrency, isForeignReceipt, fromStatementLine]);

  const handlePostClick = useCallback(() => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(`[data-field="${Object.keys(e)[0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    setErrors({});
    setSaveConfirmOpen(true);
  }, [validate]);

  const buildLines = useCallback((): CreateReceiptVoucherLineRequest[] => {
    const out: CreateReceiptVoucherLineRequest[] = [];
    parties.forEach(s => {
      const t = partyTotals.find(x => x.key === s.key)!;
      s.bills.forEach(b => {
        const a = num(b.allocated);
        const sh = billShort(b);
        // A short-paid bill posts even when nothing was received for it (ADR-0060): it closes,
        // and the whole amount stays open as a short-paid item.
        if (a > 0 || sh > 0) {
          const cross = b.currencyCode !== rcptCurrency;
          out.push({
            lineType: RECEIPT_LINE_TYPES.BILL_SETTLEMENT,
            partyId: s.partyId, settledOpenItemId: b.openItemId, amount: a,
            settledFcAmount: cross && a > 0 ? num(b.settling) : null,
            shortPaidAmount: sh > 0 ? sh : null,
            shortPaidReasonCode: sh > 0 ? b.reason : null,
            shortPaidRemarks: sh > 0 ? (b.remarks.trim() || null) : null,
          });
        }
      });
      if (t.remainder > 0) {
        out.push({ lineType: RECEIPT_LINE_TYPES.ON_ACCOUNT, partyId: s.partyId, receiveAs: s.receiveAs, amount: t.remainder });
      }
    });
    glRows.forEach(g => {
      out.push({
        lineType: RECEIPT_LINE_TYPES.GL_LINE, ledgerId: g.ledgerId, amount: num(g.amount),
        isCredit: g.isCredit, narration: g.narration.trim() || null,
      });
    });
    return out;
  }, [parties, partyTotals, glRows, rcptCurrency]);

  const handleConfirmPost = useCallback(async () => {
    setSaveConfirmOpen(false);
    setPosting(true);
    try {
      const payload: CreateReceiptVoucherRequest = {
        receiptDate: form.receiptDate,
        partyId: parties[0]?.partyId || null,
        depositToLedgerId: form.depositToLedgerId,
        currencyCode: form.currencyCode || functionalCurrency || null,
        exchangeRate: isForeignReceipt ? Number(form.exchangeRate) : 1,
        instrumentType: form.instrumentType || null,
        instrumentNumber: form.instrumentNumber.trim() || null,
        instrumentDate: form.instrumentDate || null,
        drawerBank: form.drawerBank.trim() || null,
        narration: form.narration.trim() || null,
        lines: buildLines(),
      };
      if (onSubmitOverride) {
        // Posted and matched by the statement; the account and currency are the line's.
        await onSubmitOverride({ ...payload, currencyCode: null });
        setPosting(false);
        notificationService.toast.success('Posted and matched to the statement line.');
        onPosted(); onClose();
        return;
      }
      const saved = await receiptVoucherService.create(payload);
      setPosting(false);
      notificationService.toast.success(`Receipt ${saved.receiptNumber || ''} posted — ${money(saved.netReceived)} received.`);
      onPosted(); onClose();
    } catch (e: any) {
      setPosting(false);
      notificationService.toast.error(e?.response?.data?.message || 'Could not post the receipt.', 7000);
    }
  }, [form, parties, functionalCurrency, isForeignReceipt, buildLines, onPosted, onClose, onSubmitOverride]);

  if (!isOpen) return null;

  const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
  const cellInput: React.CSSProperties = { width: '100%', textAlign: 'right', fontSize: '0.8125rem', padding: '0.3rem 0.45rem' };
  const sectHead: React.CSSProperties = {
    background: 'var(--bg-tertiary)', padding: '0.5rem 0.7rem', display: 'flex', flexWrap: 'wrap',
    alignItems: 'center', gap: '0.6rem', borderBottom: '1px solid var(--border-color)',
  };
  const sectBox: React.CSSProperties = { border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'hidden', marginTop: '1rem' };
  const isCash = form.instrumentType === 'CASH';
  const fxColor = (fx: number) => (fx > 0 ? 'var(--success-text, #15803d)' : 'var(--danger-text, #b91c1c)');

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {posting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Posting…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{fromStatementLine ? 'Receipt Voucher — from bank statement' : 'Receipt Voucher'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={posting}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={posting}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {fromStatementLine && (
            <div style={{ fontSize: '0.8125rem', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.3)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem' }}>
              From the bank statement: {fromStatementLine.date} · <strong>{money(fromStatementLine.amount)}</strong>{fromStatementLine.description ? ` · ${fromStatementLine.description}` : ''}.
              The receipt must net exactly this amount; it is matched to the line when posted.
            </div>
          )}
          {/* ---- Header ---- */}
          <div className="form-grid-4" style={{ alignItems: 'flex-end' }}>
            <div data-field="receiptDate">
              <label style={labelStyle}>Receipt Date <span className="text-danger">*</span></label>
              <FyDateInput className={`form-input${err('receiptDate') ? ' form-field-error' : ''}`}
                value={form.receiptDate}
                onChange={v => { clearErr('receiptDate'); updateForm({ receiptDate: v }); }}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              {err('receiptDate') && <div className="form-error-message">{err('receiptDate')}</div>}
            </div>
            <div data-field="depositToLedgerId">
              <SyutSelect label={<>Deposited To <span className="text-danger">*</span></>} isAsync defaultOptions cacheOptions={false} isDisabled={!!fromStatementLine}
                loadOptions={loadCashBank} menuPortalTarget={document.body}
                seedOptions={form.depositToLedgerId ? [{ value: form.depositToLedgerId, label: form.depositToLedgerLabel }] : undefined}
                value={form.depositToLedgerId}
                onChange={v => {
                  const id = (v as string) || '';
                  clearErr('depositToLedgerId'); clearErr('exchangeRate');
                  // Choosing the account chooses the currency; a home-currency account resets the rate to 1.
                  const ccy = ledgerCurrencyCache.current.get(id) || '';
                  const foreign = !!ccy && !!functionalCurrency && ccy !== functionalCurrency;
                  updateForm({ depositToLedgerId: id, depositToLedgerLabel: ledgerLabelCache.current.get(id) || '', currencyCode: ccy, exchangeRate: foreign ? '' : '1' });
                }}
                placeholder="Cash / bank account" error={err('depositToLedgerId')} isClearable={false} size="sm" />
            </div>
            {isForeignReceipt && (
              <div data-field="exchangeRate">
                <label style={labelStyle}>Rate: 1 {form.currencyCode} = ? {functionalCurrency} <span className="text-danger">*</span></label>
                <input type="number" step="0.000001" min="0"
                  className={`form-input${err('exchangeRate') ? ' form-field-error' : ''}`}
                  value={form.exchangeRate}
                  onChange={e => { clearErr('exchangeRate'); updateForm({ exchangeRate: e.target.value }); }}
                  placeholder="Today's rate"
                  title="Bills clear at the rate they were booked at; the difference against this rate is posted as an exchange gain or loss."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                {err('exchangeRate') && <div className="form-error-message">{err('exchangeRate')}</div>}
              </div>
            )}
            <div>
              <SyutSelect label="Received By" options={modeOptions} value={form.instrumentType}
                onChange={v => updateForm({ instrumentType: (v as string) || 'TRANSFER' })}
                menuPortalTarget={document.body} isClearable={false} size="sm" />
            </div>
            <div>
              <label style={labelStyle}>{isCash ? 'Narration' : 'Reference No'}</label>
              <input type="text" className="form-input"
                value={isCash ? form.narration : form.instrumentNumber}
                onChange={e => updateForm(isCash ? { narration: e.target.value } : { instrumentNumber: e.target.value })}
                placeholder={isCash ? 'What this receipt is for' : 'e.g. transfer ref / cheque no'}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
          </div>

          {!isCash && (
            <div className="form-grid-3" style={{ marginTop: '0.7rem', alignItems: 'flex-end' }}>
              <div>
                <label style={labelStyle}>Instrument Date</label>
                <input type="date" className="form-input" value={form.instrumentDate}
                  onChange={e => updateForm({ instrumentDate: e.target.value })}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div>
                <label style={labelStyle}>Payer's Bank</label>
                <input type="text" className="form-input" value={form.drawerBank}
                  onChange={e => updateForm({ drawerBank: e.target.value })}
                  placeholder="Drawer / remitting bank"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div>
                <label style={labelStyle}>Narration</label>
                <input type="text" className="form-input" value={form.narration}
                  onChange={e => updateForm({ narration: e.target.value })}
                  placeholder="What this receipt is for"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
          )}

          {/* ---- Empty state ---- */}
          {!anySection && (
            <div data-field="sections" style={{
              border: '1px dashed var(--form-border-color)', borderRadius: '0.5rem', padding: '1.5rem 1rem', textAlign: 'center',
              background: 'var(--bg-secondary)', marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem', alignItems: 'center',
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>What is this money for?</strong>&nbsp;Pick one to start — you can add the other later.
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={addParty} style={{ textAlign: 'left', padding: '0.7rem 1rem', minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>A customer or party</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Settle invoices, take an advance, receive a refund</div>
                </button>
                <button className="btn btn-secondary" onClick={() => addGlRow(true)} style={{ textAlign: 'left', padding: '0.7rem 1rem', minWidth: 220 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>An account directly</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Interest, other income, a deposit returned</div>
                </button>
              </div>
              {err('sections') && <div className="form-error-message">{err('sections')}</div>}
            </div>
          )}

          {/* ---- Party sections ---- */}
          {parties.map(s => {
            const t = partyTotals.find(x => x.key === s.key)!;
            const sErr = err(`party-${s.key}`);
            const showCcy = isForeignReceipt || s.bills.some(b => b.currencyCode !== rcptCurrency);
            const sectionFx = r2(s.bills.reduce((x, b) => x + billFx(b), 0));
            return (
              <div key={s.key} style={sectBox} data-field={`party-${s.key}`}>
                <div style={sectHead}>
                  <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                    <SyutSelect isAsync defaultOptions cacheOptions={false} loadOptions={loadParties} menuPortalTarget={document.body}
                      seedOptions={s.partyId ? [{ value: s.partyId, label: s.partyLabel }] : undefined}
                      value={s.partyId} onChange={v => pickParty(s.key, (v as string) || '')}
                      placeholder="Who paid?" isClearable size="sm" />
                  </div>
                  {s.partyId && (
                    <span className="badge">{s.loadingBills ? 'Loading…' : s.bills.length ? `${s.bills.length} outstanding` : 'nothing outstanding'}</span>
                  )}
                  <OutstandingDisplay partyId={s.partyId} side="receivable" variant="inline" />
                  <span style={{ flex: '1 1 auto' }} />
                  <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                    onClick={() => setParties(p => p.filter(x => x.key !== s.key))}>Remove</button>
                </div>

                <div style={{ padding: '0.7rem' }}>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                    <div style={{ width: 170 }}>
                      <label style={labelStyle}>Amount received</label>
                      <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                        value={s.amount} placeholder="0.00"
                        onChange={e => { clearErr(`party-${s.key}`); updateParty(s.key, { amount: e.target.value }); }} />
                    </div>
                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}
                      disabled={!s.bills.length} onClick={() => autoAllocate(s.key)}>Allocate oldest first</button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}
                      disabled={!s.bills.length || num(s.amount) <= 0}
                      title="Tick the bills the customer says they paid, enter the amount received, then click: the money fills the ticked bills oldest first and whatever each one is short becomes its short-paid amount — every ticked bill closes"
                      onClick={() => closeTickedInFull(s.key)}>Close ticked bills in full</button>
                    <span style={{ flex: '1 1 auto' }} />
                    {t.remainder > 0 && (
                      <div style={{ width: 210 }}>
                        <SyutSelect label="Unallocated goes to" options={RECEIVE_AS_OPTIONS} value={s.receiveAs}
                          onChange={v => updateParty(s.key, { receiveAs: (v as string) || RECEIVE_AS.CUSTOMER_ADVANCE })}
                          menuPortalTarget={document.body} isClearable={false} size="sm" />
                      </div>
                    )}
                  </div>

                  {s.bills.length > 0 && (
                    <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                          <th style={{ width: 32, padding: '0.4rem' }} />
                          <th style={{ textAlign: 'left', padding: '0.4rem' }}>Reference</th>
                          <th style={{ width: 110, textAlign: 'left', padding: '0.4rem' }}>Due</th>
                          <th style={{ width: showCcy ? 150 : 130, textAlign: 'right', padding: '0.4rem' }}>Outstanding{showCcy ? ' (bill ccy)' : ''}</th>
                          {showCcy && <th style={{ width: 140, textAlign: 'right', padding: '0.4rem' }}>Settling (bill ccy)</th>}
                          <th style={{ width: 130, textAlign: 'right', padding: '0.4rem' }}>Receiving{showCcy ? ` (${rcptCurrency})` : ''}</th>
                          <th style={{ width: 120, textAlign: 'right', padding: '0.4rem' }}
                            title="What the customer deducted. The bill closes for it, and it stays open on their ledger as a short-paid item to resolve later.">
                            Short-paid{showCcy ? ' (bill ccy)' : ''}
                          </th>
                          <th style={{ width: 150, textAlign: 'left', padding: '0.4rem' }}>Reason</th>
                          <th style={{ width: showCcy ? 140 : 120, textAlign: 'right', padding: '0.4rem' }}>Still open</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.bills.map(b => {
                          const alloc = num(b.allocated);
                          const cross = b.currencyCode !== rcptCurrency;
                          const fcSettling = billFcSettling(b);
                          const shortFc = billShort(b);
                          const fcOpen = r2(b.fcOutstanding - fcSettling - shortFc);
                          const fx = billFx(b);
                          return (
                            <React.Fragment key={b.openItemId}>
                            <tr style={{ borderBottom: shortFc > 0 ? 'none' : '1px solid var(--border-color)' }}>
                              <td style={{ textAlign: 'center' }}>
                                <input type="checkbox" checked={alloc > 0 || fcSettling > 0 || shortFc > 0} aria-label={`Settle ${b.referenceNo}`}
                                  onChange={e => toggleBill(s.key, b.openItemId, e.target.checked)} />
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem' }}>
                                {b.referenceNo}
                                {b.deductionNote && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' }}>{b.deductionNote}</div>
                                )}
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem' }}>{b.dueDate || '—'}</td>
                              <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {showCcy && <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{b.currencyCode}</span>}
                                {money(b.fcOutstanding)}
                                {/* Home-currency value at the booked rate, wherever a foreign figure shows. */}
                                {b.currencyCode !== functionalCurrency && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                    {functionalCurrency} {money(b.baseOutstanding)} @ {billRateOf(b).toFixed(4)}
                                  </div>
                                )}
                              </td>
                              {showCcy && (
                                <td style={{ padding: '0.25rem 0.4rem' }}>
                                  {cross ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{b.currencyCode}</span>
                                      <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                        value={b.settling} placeholder="—"
                                        onChange={e => { clearErr(`party-${s.key}`); setBillAmount(s.key, b.openItemId, 'settling', e.target.value); }} />
                                    </div>
                                  ) : (
                                    <div style={{ textAlign: 'right', color: 'var(--text-muted)' }} title="Same currency as the receipt — the amount received is the amount settled">
                                      {alloc > 0 ? money(Math.min(alloc, b.fcOutstanding)) : '—'}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                  value={b.allocated} placeholder="—"
                                  onChange={e => { clearErr(`party-${s.key}`); setBillAmount(s.key, b.openItemId, 'receiving', e.target.value); }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                                  value={b.short} placeholder="—" aria-label={`Short-paid on ${b.referenceNo}`}
                                  onChange={e => { clearErr(`party-${s.key}`); setShort(s.key, b.openItemId, e.target.value); }} />
                              </td>
                              <td style={{ padding: '0.25rem 0.4rem' }}>
                                {shortFc > 0 ? (
                                  <SyutSelect options={reasonOptions} value={b.reason}
                                    onChange={v => { clearErr(`party-${s.key}`); setShortField(s.key, b.openItemId, { reason: (v as string) || '' }); }}
                                    menuPortalTarget={document.body} placeholder="Pick a reason" isClearable={false} size="sm"
                                    error={!b.reason && err(`party-${s.key}`) ? ' ' : undefined} />
                                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: fcOpen > 0.004 ? undefined : 'var(--text-muted)' }}>
                                {fcOpen > 0.004 ? <>{showCcy && <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{b.currencyCode}</span>}{money(fcOpen)}</> : 'closed'}
                                {shortFc > 0 && fcOpen <= 0.004 && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--warning-text, #92400e)' }}>{b.currencyCode} {money(shortFc)} short-paid stays open</div>
                                )}
                                {Math.abs(fx) >= 0.005 && (
                                  <div style={{ fontSize: '0.7rem', color: fxColor(fx) }}>FX {fx > 0 ? 'gain' : 'loss'} {money(Math.abs(fx))} {functionalCurrency}</div>
                                )}
                              </td>
                            </tr>
                            {shortFc > 0 && (
                              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--warning-bg, #fef3c7)' }}>
                                <td />
                                <td colSpan={showCcy ? 7 : 6} style={{ padding: '0.25rem 0.4rem 0.4rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Short-paid remarks</span>
                                    <input type="text" className="form-input" maxLength={500}
                                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.45rem' }}
                                      value={b.remarks} placeholder="e.g. claim no., GRN, what the customer said"
                                      aria-label={`Remarks for the short-paid amount on ${b.referenceNo}`}
                                      onChange={e => setShortField(s.key, b.openItemId, { remarks: e.target.value })} />
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {s.partyId && !s.loadingBills && !s.bills.length && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      Nothing outstanding for this party — the whole amount will be recorded as
                      {' '}{s.receiveAs === RECEIVE_AS.SUPPLIER_REFUND ? 'a supplier refund' : 'a customer advance'}.
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.55rem', fontSize: '0.8rem' }}>
                    <span style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className="badge" style={t.remainder > 0 ? { background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' } : undefined}>
                        {t.remainder > 0
                          ? `${rcptCurrency} ${money(t.remainder)} unallocated — ${s.receiveAs === RECEIVE_AS.SUPPLIER_REFUND ? 'supplier refund' : 'customer advance'}`
                          : 'Fully allocated'}
                      </span>
                      {t.shortCount > 0 && (
                        <span className="badge" style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}
                          title="Each short-paid amount stays open on this party's ledger, with its reason, until you clear it — a later receipt, a credit note set off against it, or a write-off.">
                          {functionalCurrency} {money(t.shortBase)} short-paid on {t.shortCount} bill{t.shortCount > 1 ? 's' : ''} — kept open on their ledger
                        </span>
                      )}
                    </span>
                    <span>Allocated <strong>{rcptCurrency} {money(t.allocated)}</strong> of <strong>{rcptCurrency} {money(t.total)}</strong></span>
                  </div>
                  {Math.abs(sectionFx) >= 0.005 && (
                    <div style={{ marginTop: '0.35rem', fontSize: '0.78rem', color: fxColor(sectionFx) }}>
                      Exchange {sectionFx > 0 ? 'gain' : 'loss'} <strong>{money(Math.abs(sectionFx))} {functionalCurrency}</strong> on these
                      settlements — posts {sectionFx > 0 ? 'Cr' : 'Dr'} FX Gain / Loss (bills clear at their booked rate; the money arrives at {money(num(form.exchangeRate) || 1)}).
                    </div>
                  )}
                  {sErr && <div className="form-error-message">{sErr}</div>}
                </div>
              </div>
            );
          })}

          {/* ---- Accounts ---- */}
          {glRows.length > 0 && (
            <div style={sectBox}>
              <div style={sectHead}>
                <strong style={{ fontSize: '0.85rem' }}>Accounts</strong>
                <span className="badge">Other income adds · WHT, bank charges, discount reduce</span>
                <span style={{ flex: '1 1 auto' }} />
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }} onClick={() => setGlRows([])}>Remove</button>
              </div>
              <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                    <th style={{ width: 30, padding: '0.4rem' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem' }}>Account</th>
                    <th style={{ width: 200, textAlign: 'left', padding: '0.4rem' }}>Narration</th>
                    <th style={{ width: 120, textAlign: 'right', padding: '0.4rem' }}>Amount</th>
                    <th style={{ width: 150, textAlign: 'center', padding: '0.4rem' }}>Effect on receipt</th>
                    <th style={{ width: 34, padding: '0.4rem' }} />
                  </tr>
                </thead>
                <tbody>
                  {glRows.map((g, i) => (
                    <React.Fragment key={g.key}>
                      <tr data-field={`gl-${g.key}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{i + 1}</td>
                        <td style={{ padding: '0.25rem 0.4rem', minWidth: 220 }}>
                          <SyutSelect isAsync defaultOptions cacheOptions={false} loadOptions={loadLedgers} menuPortalTarget={document.body}
                            seedOptions={g.ledgerId ? [{ value: g.ledgerId, label: g.ledgerLabel }] : undefined}
                            value={g.ledgerId}
                            onChange={v => { const id = (v as string) || ''; clearErr(`gl-${g.key}`); updateGl(g.key, { ledgerId: id, ledgerLabel: ledgerLabelCache.current.get(id) || '' }); }}
                            placeholder="Account to post to" isClearable size="sm" />
                        </td>
                        <td style={{ padding: '0.25rem 0.4rem' }}>
                          <input type="text" className="form-input" style={{ fontSize: '0.8125rem', padding: '0.3rem 0.45rem' }}
                            value={g.narration} placeholder="e.g. Bank charges" onChange={e => updateGl(g.key, { narration: e.target.value })} />
                        </td>
                        <td style={{ padding: '0.25rem 0.4rem' }}>
                          <input type="number" min={0} step="0.01" className="form-input" style={cellInput}
                            value={g.amount} placeholder="0.00"
                            onChange={e => { clearErr(`gl-${g.key}`); updateGl(g.key, { amount: e.target.value }); }} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '0.25rem 0.4rem' }}>
                          <button onClick={() => updateGl(g.key, { isCredit: !g.isCredit })}
                            title={g.isCredit ? 'Adds to the money received — click to switch' : 'Reduces the money received — click to switch'}
                            style={{
                              border: '1px solid var(--form-border-color)', borderRadius: '0.35rem', background: 'transparent', cursor: 'pointer',
                              padding: '0.22rem 0.45rem', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                              color: g.isCredit ? '#16a34a' : '#dc2626',
                            }}>
                            {g.isCredit ? '+ adds (Cr)' : '− reduces (Dr)'}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button title="Remove line" onClick={() => setGlRows(p => p.filter(x => x.key !== g.key))}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                            <IconSystem.trash size={15} />
                          </button>
                        </td>
                      </tr>
                      {err(`gl-${g.key}`) && (
                        <tr><td /><td colSpan={5} style={{ paddingBottom: '0.4rem' }}><div className="form-error-message">{err(`gl-${g.key}`)}</div></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '0.5rem 0.7rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', background: 'var(--bg-secondary)' }}>
                <button className="btn btn-secondary" onClick={() => addGlRow(true)} style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                  <IconSystem.add size={13} /> Income line
                </button>
                <button className="btn btn-secondary" onClick={() => addGlRow(false)} style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                  <IconSystem.add size={13} /> Deduction (WHT, charges)
                </button>
              </div>
            </div>
          )}

          {/* ---- Add bar ---- */}
          {anySection && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={addParty} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                <IconSystem.add size={14} /> Payer
              </button>
              {glRows.length === 0 && (
                <>
                  <button className="btn btn-secondary" onClick={() => addGlRow(true)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                    <IconSystem.add size={14} /> Accounts
                  </button>
                  <button className="btn btn-secondary" onClick={() => addGlRow(false)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                    <IconSystem.add size={14} /> Deduction
                  </button>
                </>
              )}
            </div>
          )}

          {err('sections') && anySection && <div className="form-error-message" data-field="sections">{err('sections')}</div>}

          {/* ---- Net received ---- */}
          <div style={{
            position: 'sticky', bottom: 0, marginTop: '1rem', padding: '0.65rem 0.9rem', background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
              <span>Received for <strong>{money(totals.gross)}</strong></span>
              <span style={{ color: totals.reduces ? '#dc2626' : 'var(--text-muted)' }}>Deductions <strong>{money(totals.reduces)}</strong></span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                Net Received&nbsp;<span style={{ color: totals.net > 0 ? 'var(--text-primary)' : '#dc2626' }}>{rcptCurrency} {money(totals.net)}</span>
              </div>
              {isForeignReceipt && num(form.exchangeRate) > 0 && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  ≈ {functionalCurrency} {money(r2(totals.net * num(form.exchangeRate)))} at {money(num(form.exchangeRate))}
                </div>
              )}
              {Math.abs(totalFx) >= 0.005 && (
                <div style={{ fontSize: '0.72rem', color: fxColor(totalFx) }}>
                  incl. exchange {totalFx > 0 ? 'gain' : 'loss'} {money(Math.abs(totalFx))} {functionalCurrency} ({totalFx > 0 ? 'Cr' : 'Dr'} FX Gain / Loss)
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={posting}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePostClick} disabled={posting || totals.net <= 0}>
            <IconSystem.check size={16} /> Post Receipt
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title="Post Receipt"
        message={`Record ${rcptCurrency} ${money(totals.net)} received into ${form.depositToLedgerLabel || 'the selected account'}?${
          Math.abs(totalFx) >= 0.005 ? ` Bills clear at their booked rates; an exchange ${totalFx > 0 ? 'gain' : 'loss'} of ${functionalCurrency} ${money(Math.abs(totalFx))} will post.` : ''}`}
        confirmLabel="Post" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmPost} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL — read-only posted receipt.
// ============================================================================

const LINE_KIND_LABEL: Record<string, string> = {
  [RECEIPT_LINE_TYPES.BILL_SETTLEMENT]: 'Bill settled',
  [RECEIPT_LINE_TYPES.ON_ACCOUNT]: 'On account',
  [RECEIPT_LINE_TYPES.GL_LINE]: 'Account',
};
const RECEIVE_AS_LABEL: Record<string, string> = {
  [RECEIVE_AS.CUSTOMER_ADVANCE]: 'customer advance',
  [RECEIVE_AS.SUPPLIER_REFUND]: 'supplier refund',
};

export interface ReceiptVoucherViewModalProps {
  isOpen: boolean;
  receipt: ReceiptVoucherDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (receiptNumber: string) => void;
}

export const ReceiptVoucherViewModal: React.FC<ReceiptVoucherViewModalProps> = ({
  isOpen, receipt, onClose, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<ReceiptVoucherDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !receipt?.id) { setDetail(null); return; }
    setLoading(true);
    receiptVoucherService.getById(receipt.id).then(setDetail).catch(() => setDetail(receipt)).finally(() => setLoading(false));
  }, [isOpen, receipt]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !receipt?.receiptNumber) return;
    minimize({
      id: `${tabId}-view-modal-${receipt.receiptNumber}`,
      title: `View - ${receipt.receiptNumber}`,
      moduleName: moduleName || 'Receipt Vouchers', tabId,
      onRestore: () => onRestoreView?.(receipt.receiptNumber!),
    });
    onClose();
  }, [tabId, moduleName, receipt, minimize, onClose, onRestoreView]);

  if (!isOpen || !receipt) return null;
  const r = detail ?? receipt;
  const baseCcy = r.baseCurrencyCode || '';
  const viewIsForeign = !!r.currencyCode && !!baseCcy && r.currencyCode !== baseCcy;
  const totalRealizedFx = (r.lines || []).reduce((s, l) => s + (l.realizedFxAmount || 0), 0);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Receipt — {r.receiptNumber || '(unnumbered)'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8125rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div><strong>{(r.receiptDate || '').slice(0, 10)}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Received From</div><strong>{r.partyName || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Deposited To</div><strong>{r.depositToLedgerName || '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div><span className="badge">{r.docStatus}</span></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Instrument</div><strong>{r.instrumentType || '—'}{r.instrumentNumber ? ` · ${r.instrumentNumber}` : ''}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Payer's Bank</div><strong>{r.drawerBank || '—'}</strong></div>
            {viewIsForeign && <div><div style={{ color: 'var(--text-muted)' }}>Received in</div><strong>{r.currencyCode} @ {r.exchangeRate}</strong></div>}
            <div style={{ gridColumn: viewIsForeign ? 'span 1' : 'span 2' }}><div style={{ color: 'var(--text-muted)' }}>Narration</div><strong>{r.narration || '—'}</strong></div>
          </div>

          <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
                <th style={{ width: 160, textAlign: 'left', padding: '0.5rem' }}>Kind</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Details</th>
                <th style={{ width: 160, textAlign: 'left', padding: '0.5rem' }}>Business Unit</th>
                <th style={{ width: 150, textAlign: 'right', padding: '0.5rem' }}>Amount ({baseCcy})</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading…</td></tr>}
              {!loading && (r.lines || []).map((l, idx) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {LINE_KIND_LABEL[l.lineType] || l.lineType}
                    {l.receiveAs && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{RECEIVE_AS_LABEL[l.receiveAs] || l.receiveAs}</div>}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {[l.ledgerCode ? `${l.ledgerCode} - ${l.ledgerName}` : l.ledgerName, l.settledReferenceNo, l.partyName, l.narration].filter(Boolean).join(' · ') || '—'}
                    {!!l.shortPaidFcAmount && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--warning-text, #92400e)', marginTop: 2 }}>
                        Short-paid {l.settledCurrency || baseCcy} {money(l.shortPaidFcAmount)}
                        {l.shortPaidReasonLabel || l.shortPaidReasonCode ? ` · ${l.shortPaidReasonLabel || l.shortPaidReasonCode}` : ''}
                        {l.shortPaidRemarks ? ` · ${l.shortPaidRemarks}` : ''} — the bill closed; this amount stays open on the party's ledger
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{l.businessUnitName || '—'}</td>
                  <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>
                    {isReceiptPartyLine(l.lineType) || l.isCredit ? money(l.amount) : `(${money(l.amount)})`}
                    {l.fcAmount != null && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{r.currencyCode} {money(l.fcAmount)}</div>}
                    {l.settledFcAmount != null && l.settledCurrency && l.settledCurrency !== baseCcy && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>settled {l.settledCurrency} {money(l.settledFcAmount)}</div>
                    )}
                    {!!l.realizedFxAmount && (
                      <div style={{ fontSize: '0.7rem', color: l.realizedFxAmount < 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>
                        {l.realizedFxAmount < 0 ? 'FX loss' : 'FX gain'} {money(Math.abs(l.realizedFxAmount))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>Received for</td>
                <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>{money(r.grossAmount)}</td>
              </tr>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>Deductions</td>
                <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>{money(r.deductionTotal)}</td>
              </tr>
              {!!totalRealizedFx && (
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <td colSpan={4} style={{ textAlign: 'right', padding: '0.375rem 0.5rem' }}>Exchange {totalRealizedFx < 0 ? 'loss' : 'gain'} on settlement</td>
                  <td style={{ textAlign: 'right', padding: '0.375rem 0.5rem', color: totalRealizedFx < 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>
                    {money(Math.abs(totalRealizedFx))}
                  </td>
                </tr>
              )}
              <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)' }}>
                <td colSpan={4} style={{ textAlign: 'right', padding: '0.5rem' }}>Net Received ({baseCcy})</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(r.netReceived)}</td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Posted{r.createdByName ? ` by ${r.createdByName}` : ''}{r.createdAt ? ` on ${new Date(r.createdAt).toLocaleString()}` : ''}.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default ReceiptVoucherFormModal;
